import React, { useState } from 'react';
import { ICompletionStatus, IWorksheetPurchaserInfo } from '../../types/worksheet';
import styled from 'styled-components';
import { useSpring, animated } from 'react-spring';
import { MenuItem, FormControl, InputLabel, Button, Checkbox, FormControlLabel, FormHelperText, Grid } from '@mui/material';
import Select from '@mui/material/Select';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { WorksheetTextField, smWidth, Heading } from '../../utils/CommonStyles';
import { sanitizeSIN } from '../../utils/Validations';
import { formatSin } from '../../utils/Functions';

const Identification = (props: ChildProps) => {
  const {
    completionStatus,
    fields,
    purchaserCount,
    purchaserNumber,
    disabled,
    handleTextInput,
    handleDateChange,
    handleAddPurchaser,
    handlePurchaserTypeChange,
    handleIdTypeChange,
    handleAcknowledgement,
    handleText,
    noCheckbox,
    asterisk,
    submitted,
  } = props;
  const { idType, idNumber, idExpiry, idJurisdiction, sin, purchaserType, acknowledgement, corp } = fields;
  const [dateError, setDateError] = useState<string | null>(null);

  const idTypeMissing = !!submitted && !idType;
  const idNumberMissing = !!submitted && !idNumber;
  const idExpiryMissing = !!submitted && !idExpiry;
  const idJurisdictionMissing = !!submitted && !idJurisdiction;
  const purchaserTypeMissing = !!submitted && !purchaserType;
  const sinHasError = !!submitted && !disabled && sin.length !== 11 && sin !== '';
  const sinMissing = !!submitted && !sin;

  const selectStyle = {
    width: '100%',
    '@media (max-width: 480px)': {
      width: '100%',
      '.MuiInputBase-root': {
        width: '100%',
      },
    },
    '& .MuiFormLabel-asterisk.MuiInputLabel-asterisk': {
      color: 'red',
    },
  };

  const showButtons = completionStatus.addressDetails && purchaserNumber + 1 === purchaserCount;

  const animationProps = useSpring({
    to: { opacity: completionStatus.addressDetails ? 1 : 0, y: completionStatus.addressDetails ? 0 : 100 },
    from: { opacity: 0, y: 100 },
    delay: 250,
  });

  const locations = [
    'Quebec',
    'Saskatchewan',
    'Manitoba',
    'Alberta',
    'British Columbia',
    'Prince Edward Island',
    'New Brunswick',
    'Newfoundland and Labrador',
    'Nunavut',
    'Northwest Territories',
    'Yukon Territory',
    'Nova Scotia',
    'International Government',
    '',
  ];

  const IdTypes = [
    "Driver's License",
    'Photo Card',
    'Passport',
    'Permanent Resident Card',
    'Identity Card',
    'Articles of Incorporation',
  ];

  const InputFields = (
    <Grid container spacing={2}>
      <Grid item lg={4} md={4} sm={4} xs={12}>
        <FormControl sx={selectStyle} disabled={disabled} error={idTypeMissing}>
          <InputLabel id="demo-simple-select-label" required>ID Type</InputLabel>
          <Select labelId="demo-simple-select-label" id="idType" value={idType} label="ID Type" onChange={handleIdTypeChange} required>
            {IdTypes.map((IdType: string) => {
              return <MenuItem value={`${IdType}`}>{IdType}</MenuItem>;
            })}
          </Select>
          {idTypeMissing && <FormHelperText>Missing ID Type</FormHelperText>}
        </FormControl>
      </Grid>
      <Grid item lg={4} md={4} sm={4} xs={12}>
        <WorksheetTextField
          id="idnumber-textarea"
          label="ID Number"
          name="idNumber"
          sx={asterisk}
          error={idNumberMissing}
          helperText={idNumberMissing ? 'Missing ID Number' : ''}
          value={idNumber}
          onChange={handleTextInput}
          disabled={disabled}
          required
        />
      </Grid>
      <Grid item lg={4} md={4} sm={4} xs={12}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Expiry Date"
            value={idExpiry}
            onChange={(newValue: any) => {
              handleDateChange(newValue, 'idExpiry');
            }}
            minDate={new Date(new Date().setHours(0, 0, 0, 0))}
            onError={(reason: any, value: any) => {
              switch (reason) {
                case 'invalidDate':
                  setDateError('Invalid Date');
                  break;
                case 'disablePast':
                  setDateError('Invalid Date');
                  break;
                case 'disableFuture':
                  setDateError('Purchaser cannot born in the future!');
                  break;
                case 'maxDate':
                  setDateError('Invalid Date');
                  break;
                case 'minDate':
                  setDateError('ID has expired!');
                  break;
                case 'shouldDisableDate':
                  // shouldDisableDate returned true, render custom message according to the `shouldDisableDate` logic
                  setDateError('Invalid Date');
                  break;
                default:
                  setDateError(null);
              }
            }}
            sx={{ width: '100%', ...asterisk }}
            disabled={disabled}
            slotProps={{
              textField: {
                required: true,
                error: idExpiryMissing,
                helperText: idExpiryMissing ? 'Missing ID Expiry' : '',
              },
            }}
          />
          {dateError ? <DateError>{dateError}</DateError> : null}
        </LocalizationProvider>
      </Grid>

      <Grid item lg={4} md={4} sm={4} xs={12}>
        <FormControl
          sx={{
            width: '100%',
            '@media (max-width: 480px)': {
              width: '100%',
              '.MuiInputBase-root': {
                width: '100%',
              },
            },
          }}
          disabled={disabled}
          error={idJurisdictionMissing}
        >
          <InputLabel id="demo-simple-select-label" required>ID Issued By</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            name="idJurisdiction"
            id="idissuedby-textarea"
            label="ID Issued By"
            value={idJurisdiction}
            onChange={handleTextInput}
            disabled={disabled}
            required
          >
            <MenuItem value={'Ontario'} selected>
              Ontario
            </MenuItem>
            {locations.map((location: string) => {
              return <MenuItem value={`${location}`}>{location}</MenuItem>;
            })}
          </Select>
          {idJurisdictionMissing && <FormHelperText>Missing ID Issued By</FormHelperText>}
        </FormControl>
      </Grid>
      <Grid item lg={4} md={4} sm={4} xs={12}>
        <WorksheetTextField
          id="sinnumber-textarea"
          label="SIN Number (xxx xxx xxx)"
          name="sin"
          sx={asterisk}
          error={sinHasError || sinMissing}
          helperText={sinHasError ? 'Please enter a valid SIN number' : sinMissing ? 'Missing SIN Number' : ''}
          onChange={(e) => {
            handleText(e.target.name, sanitizeSIN(e.target.value));
          }}
          value={formatSin(sin)}
          disabled={disabled}
          required
        />
      </Grid>
      <Grid item lg={4} md={4} sm={4} xs={12}>
        <FormControl sx={selectStyle} disabled={disabled} error={purchaserTypeMissing}>
          <InputLabel id="demo-simple-select-label" required>Purchaser Type</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="purchaserType"
            value={purchaserType}
            label="Purchaser Type"
            onChange={handlePurchaserTypeChange}
            required
          >
            <MenuItem value={'Investor'}>Investor</MenuItem>
            <MenuItem value={'End User'}>End User</MenuItem>
            <MenuItem value={'Both'}>Both</MenuItem>
          </Select>
          {purchaserTypeMissing && <FormHelperText>Missing Purchaser Type</FormHelperText>}
        </FormControl>
      </Grid>
    </Grid>
  );

  return (
    <div>
      {corp
        ? completionStatus.signingOfficer && (
            <IdentificationContainer container spacing={2}>
              <Heading item xs={12}>
                <animated.div style={animationProps}>Identification</animated.div>
              </Heading>

              <Grid item xs={12}>
                <animated.div style={animationProps}>{InputFields}</animated.div>
                {noCheckbox ? null : (
                  <FormControlLabel
                    sx={{
                      marginTop: '10px',
                      marginBottom: '15px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      '.MuiTypography-root': {
                        marginTop: '8px',
                      },
                    }}
                    control={
                      <Checkbox
                        checked={acknowledgement}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleAcknowledgement(event)}
                      />
                    }
                    label="I acknowledge that the information submitted above is complete and correct. For identification that does not include an address, an official document stating the purchaser's address (also known as proof of address) has been included in this worksheet or will be included within 48 hours."
                  />
                )}

                <div style={{ marginTop: '5px', display: 'flex', justifyContent: 'flex-end' }}>
                  {showButtons && (
                    <Button
                      sx={{
                        '@media (max-width: 480px)': {
                          width: '100%',
                          marginBottom: '10px',
                        },
                      }}
                      disableElevation
                      onClick={handleAddPurchaser}
                      variant="contained"
                    >
                      Add Another Purchaser
                    </Button>
                  )}
                </div>
              </Grid>
            </IdentificationContainer>
          )
        : completionStatus.addressDetails && (
            <IdentificationContainer container spacing={2}>
              <Heading item xs={12}>
                <animated.div style={animationProps}>Identification</animated.div>
              </Heading>

              <Grid item xs={12}>
                <animated.div style={animationProps}>{InputFields}</animated.div>
                {noCheckbox ? null : (
                  <FormControlLabel
                    sx={{
                      marginTop: '10px',
                      marginBottom: '15px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      '.MuiTypography-root': {
                        marginTop: '8px',
                      },
                    }}
                    control={
                      <Checkbox
                        checked={acknowledgement}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleAcknowledgement(event)}
                      />
                    }
                    label="I acknowledge that the information submitted above is complete and correct. For identification that does not include an address, an official document stating the purchaser's address (also known as proof of address) has been included in this worksheet or will be included within 48 hours."
                  />
                )}

                <div style={{ marginTop: '5px', display: 'flex', justifyContent: 'flex-end' }}>
                  {showButtons && (
                    <Button
                      sx={{
                        '@media (max-width: 480px)': {
                          width: '100%',
                          marginBottom: '10px',
                        },
                      }}
                      disableElevation
                      onClick={handleAddPurchaser}
                      variant="contained"
                    >
                      Add Another Purchaser
                    </Button>
                  )}
                </div>
              </Grid>
            </IdentificationContainer>
          )}
    </div>
  );
};

interface ChildProps {
  fields: IWorksheetPurchaserInfo;
  completionStatus: ICompletionStatus;
  worksheetId?: string;
  purchaserCount: number;
  purchaserNumber: number;
  setFileImage?: any;
  handleTextInput: any;
  handleDateChange: any;
  handlePurchaserTypeChange: any;
  handleIdTypeChange: any;
  handleAcknowledgement: any;
  handleAddPurchaser: any;
  disabled?: boolean;
  subheading?: string;
  handleText: (name: string, value: string) => void;
  noCheckbox?: boolean;
  style?: object;
  asterisk?: object;
  submitted?: boolean;
}

const IdentificationContainer = styled(Grid)`
  display: flex;
  margin-top: 50px !important;

  & .MuiIconButton-root {
    margin-top: 0 !important;
    border: none !important;
    width: auto !important;
  }

  @media (max-width: ${smWidth}) {
    display: block;
    width: 100%;
  }
`;

const DateError = styled.p`
  font-size: 0.75rem;
  color: #f54394;
  margin: 3px 14px 0 14px;
`;

export default Identification;
